/* eslint-disable no-nested-ternary */
import React, { useEffect, useState } from "react";

import {
  Avatar,
  Button,
  Col,
  DatePicker,
  Input,
  message,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Typography,
} from "antd";
import fileDownload from "js-file-download";
import moment from "moment";

import useDebounce from "../../hooks/useDebounce";
import { getRefernEarnPayoutsNew } from "../api";

const formatIndianNumber = (value) => {
  return new Intl.NumberFormat("en-IN").format(value);
};

const RefernEarnPayouts = () => {
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [rangeType, setRangeType] = useState(""); // commissionPeriod, release
  const [filters, setFilters] = useState({
    transactionType: "", // credit, debit
    commissionPeriodStartDate: "",
    commissionPeriodEndDate: "",
    releaseStartDate: "",
    releaseEndDate: "",
    page: 1,
    pageSize: 10,
    search: "",
  });
  const [loading, setLoading] = useState(false);

  const debouncedSearch = useDebounce({
    value: search,
    callback: () => {},
  });
  const [tableKey, setTableKey] = useState(Date.now());

  const handleGetRefernEarnPayouts = async (csv = false) => {
    setLoading(true);
    let hide;
    if (csv) {
      hide = message.loading("Generating CSV...", 0);
    }
    try {
      const res = await getRefernEarnPayoutsNew(filters, csv);
      if (csv) {
        fileDownload(res, "refern_earn_payouts.csv");
      } else if (res?.code === 200) {
        setData(res.result.payouts.map((item, index) => ({ ...item, index })));
        setTotal(res.result.totalPayouts);
        setTableKey(Date.now());
      } else {
        message.error("Something went wrong");
      }
    } catch (err) {
      console.log(err);
      message.error("Something went wrong");
    } finally {
      if (hide) hide();
      setLoading(false);
    }
  };

  const columns = [
    {
      title: "Release Date",
      dataIndex: "releaseDate",
      key: "releaseDate",
      render: (value) => (value ? moment(value).format("DD MMM YYYY") : "-"),
    },
    {
      title: "Creator",
      dataIndex: "userId",
      key: "userId",
      render: (_, record) => (
        <Space size="small" direction="vertical">
          <Avatar src={record?.profilePicUrl} size={32} />
          <Typography style={{}}>{record?.name}</Typography>
          <Typography>{record?.email}</Typography>
          <Typography>{record?.phone}</Typography>
          <Typography>({record?.host})</Typography>
        </Space>
      ),
    },
    {
      title: "Credit",
      dataIndex: "inrAmount",
      key: "inrAmount",
      defaultSortOrder: null,
      render: (value, record) =>
        record?.transactionType === "credit" ? (
          <Statistic
            value={value}
            precision={0}
            formatter={formatIndianNumber}
            valueStyle={{ fontSize: 16, color: "green" }}
            prefix={<span className="siteInr">&#8377;</span>}
          />
        ) : (
          <span>-</span>
        ),
    },
    {
      title: "Debit",
      dataIndex: "inrAmount",
      key: "inrAmount",
      render: (value, record) =>
        record?.transactionType === "debit" ? (
          <Statistic
            value={value}
            precision={0}
            formatter={formatIndianNumber}
            valueStyle={{ fontSize: 16, color: "red" }}
            prefix={<span className="siteInr">&#8377;</span>}
          />
        ) : (
          <span>-</span>
        ),
    },
    {
      title: "Commission Period",
      dataIndex: "payoutStartDate",
      key: "payoutStartDate",
      render: (value, record) =>
        `${moment(value).format("DD MMM YYYY")} - ${moment(
          record?.payoutEndDate
        ).format("DD MMM YYYY")}`,
    },
    {
      title: "Remarks",
      dataIndex: "remark",
      key: "remark",
    },
  ];

  const handleChange = (value) => {
    setRangeType(value);
    setFilters({
      ...filters,
      commissionPeriodStartDate: "",
      commissionPeriodEndDate: "",
      releaseStartDate: "",
      releaseEndDate: "",
    });
  };

  useEffect(() => {
    handleGetRefernEarnPayouts();
  }, [filters]);

  useEffect(() => {
    if (debouncedSearch) {
      setFilters((prev) => ({
        ...prev,
        search: debouncedSearch,
        page: 1,
      }));
    }
  }, [debouncedSearch]);

  return (
    <>
      <Row gutter={6}>
        <Col span={12}>
          <Input
            minLength={2}
            className="searchbar"
            value={search}
            style={{ width: "100%" }}
            placeholder="Search by partner name, email or mobile"
            onChange={(event) => {
              if (event.target.value.length === 0) {
                setFilters({
                  ...filters,
                  search: "",
                });
              }
              setSearch(event.target.value);
            }}
          />
        </Col>
        <Col span={12}>
          <Space>
            <Select
              placeholder="Select range filter"
              onChange={handleChange}
              options={[
                {
                  value: "commissionPeriod",
                  label: "Commission Period",
                },
                {
                  value: "release",
                  label: "Release Date",
                },
              ]}
            />

            {rangeType === "commissionPeriod" ? (
              <DatePicker.RangePicker
                value={[
                  filters.commissionPeriodStartDate,
                  filters.commissionPeriodEndDate,
                ]}
                onChange={(dates) => {
                  setFilters({
                    ...filters,
                    commissionPeriodStartDate: dates?.[0].startOf("day"),
                    commissionPeriodEndDate: dates?.[1].endOf("day"),
                  });
                }}
                allowClear
              />
            ) : rangeType === "release" ? (
              <DatePicker.RangePicker
                value={[filters.releaseStartDate, filters.releaseEndDate]}
                onChange={(dates) => {
                  setFilters({
                    ...filters,
                    releaseStartDate: dates?.[0].startOf("day"),
                    releaseEndDate: dates?.[1].endOf("day"),
                  });
                }}
                allowClear
              />
            ) : null}

            <Button
              type="primary"
              loading={loading}
              onClick={() => {
                handleGetRefernEarnPayouts(true);
              }}
            >
              Export CSV
            </Button>
          </Space>
        </Col>
      </Row>
      <Row style={{ marginTop: "20px" }}>
        <Col span={12}>
          <Space>
            <Button
              type={filters.transactionType === "" ? "primary" : "default"}
              onClick={() => setFilters({ ...filters, transactionType: "" })}
            >
              All
            </Button>
            <Button
              type={
                filters.transactionType === "credit" ? "primary" : "default"
              }
              onClick={() =>
                setFilters({ ...filters, transactionType: "credit" })
              }
            >
              Credit
            </Button>
            <Button
              type={filters.transactionType === "debit" ? "primary" : "default"}
              onClick={() =>
                setFilters({ ...filters, transactionType: "debit" })
              }
            >
              Debit
            </Button>
          </Space>
        </Col>
      </Row>

      <Table
        key={tableKey}
        columns={columns}
        dataSource={data}
        loading={loading}
        style={{
          marginTop: "20px",
        }}
        pagination={{
          current: filters.page,
          total,
          pageSize: filters.pageSize,
          onChange: (p) => setFilters({ ...filters, page: p }),
        }}
        rowKey={(record) => record._id}
      />
    </>
  );
};

export default RefernEarnPayouts;
